import { useFormContext } from "react-hook-form";
import {
  FormControlDependsOnSchema,
  FormControlDependsOnThenEnum,
  getValuesPropertyMap,
  getFullPropertyPath,
  IValidation,
  isSetFocusFormControlDependsOnSchema,
  isSetValueFormControlDependsOnSchema,
  isRequiredFormControlDependsOnSchema,
  isAddValidationsFormControlDependsOnSchema,
  isDisableFormControlDependsOnSchema,
  isHideFormControlDependsOnSchema
} from "@generation/core";
import { isNullable } from "@core/utils";
export interface IUseDependsOnHookResult {
  hide?: boolean;
  disabled?: boolean;
  isRequired?: boolean;
  setValue?: ISetValue;
  addValidations?: IValidation[];
  removeValidations?: IValidation[];
  setFocus?: ISetFocus;
}

export interface ISetValue {
  value?: any;
  result?: boolean;
}

export interface ISetFocus {
  result?: boolean;
}

export const useDependsOnHook = (
  baseName: string,
  dependsOn?: FormControlDependsOnSchema[],
  enabledTypes?: FormControlDependsOnThenEnum[]
): IUseDependsOnHookResult => {
  const { watch, getValues } = useFormContext();

  if (!dependsOn) {
    return {};
  }

  let result: IUseDependsOnHookResult = {
    disabled: undefined,
    hide: undefined,
    isRequired: undefined,
    setValue: undefined,
    addValidations: undefined,
    removeValidations: undefined,
    setFocus: undefined
  };

  dependsOn
    .filter((item) => isNullable(enabledTypes) || enabledTypes.includes(item.then))
    .forEach((item) => {
      let dependsOnPropertyValue: { [key: string]: string | null } = {};

      let values: any = {};
      if (item.isAbsolutePropertyPath === true) {
        dependsOnPropertyValue = watch(item.properties);
        if (Array.isArray(dependsOnPropertyValue)) {
          values = getValuesPropertyMap(dependsOnPropertyValue, item.properties);
        } else {
          if (item.properties.length === 1) {
            values[item.properties[0]] = dependsOnPropertyValue;
          }
        }
      } else {
        const baseFieldName = `${baseName}${baseName ? "." : ""}`;
        const fieldMappings: { input: string; fullPath: string }[] = [];

        const fields = item.properties.map((property: string) => {
          const fullPath = getFullPropertyPath(baseFieldName, property);

          fieldMappings.push({
            input: property,
            fullPath: fullPath
          });

          return fullPath;
        });

        dependsOnPropertyValue = watch(fields);

        if (Array.isArray(dependsOnPropertyValue)) {
          values = getValuesPropertyMap(
            dependsOnPropertyValue,
            fields,
            undefined,
            (fieldName: string) =>
              fieldMappings.find((item) => item.fullPath === fieldName)?.input ?? fieldName
          );
        } else {
          if (fields.length === 1) {
            values[fields[0]] = dependsOnPropertyValue;
          }
        }
      }
      const ifResult = item.if(values);

      result = {
        hide: !result.hide && isHideFormControlDependsOnSchema(item) ? ifResult : result.hide,
        disabled: !result.disabled && isDisableFormControlDependsOnSchema(item) ? ifResult : result.disabled,
        addValidations:
          isAddValidationsFormControlDependsOnSchema(item) && ifResult
            ? [...(result.addValidations ?? []), ...(item.addValidations ? item.addValidations(values) : [])]
            : result.addValidations ?? [],
        removeValidations: isAddValidationsFormControlDependsOnSchema(item)
          ? [...(result.removeValidations ?? []), ...(item.addValidations ? item.addValidations(values) : [])]
          : result.removeValidations ?? [],
        isRequired:
          !result.isRequired && isRequiredFormControlDependsOnSchema(item) ? ifResult : result.isRequired,
        setValue:
          !result.setValue && isSetValueFormControlDependsOnSchema(item) && ifResult
            ? {
                value:
                  item.setWithValue instanceof Function
                    ? item.setWithValue(values, getValues())
                    : item.setWithValue,
                result: ifResult
              }
            : result.setValue,
        setFocus:
          !result.setFocus?.result && isSetFocusFormControlDependsOnSchema(item)
            ? { result: ifResult }
            : result.setFocus
      };
    });

  return result;
};
